import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useBlocker } from 'react-router-dom';

import { useMessage } from '@tmapy/intl';
import { useSelector, useDispatch, batch } from '@tmapy/redux';

import './NewOrder.scss';

import type { GeoJSONFeature } from '@tmapy/mapcore';
import { actionSetLayerFeatures } from '@tmapy/mapcore';
import { MultiSelectProps } from '@tmapy/style-guide';

import { PageTitle } from '../../app/components/PageTitle';
import {
  actionClearAreas,
  newOrderClear,
  actionSetAreaType,
  actionSetCadasters,
  actionSetCities,
  actionSetORPs,
  actionSetRegions,
  actionSetMomcs,
  actionPublishsetAddFormat,
  actionSetReason,
  actionSetHandlingFee,
  actionSetNote,
  actionSetOrigin,
  actionInitAttachments,
  AttachmentProps,
  actionSetId,
} from '../actions/newOrder';

import { NEW_ORDER_STEPS, ORDER_REGION_TYPES } from '../constants';
import { Step } from './Step';
import { OrderPublishsetList } from './OrderPublishsetList';
import { Areas } from './Areas';
import { Reason } from './Reason';
import { Summary } from './Summary';
import { newOrderMessages, orderMessages } from './orderMessages';
import { StepNavItem } from './StepNavItem';
import { useMultiSelect } from '../../hooks';
import { OmittedFormItemProps } from '../../types';
import {
  CadasterListResponse,
  KrajListResponse,
  ORPListResponse,
  ObecListResponse,
  MomcListResponse,
  OrderRetrieveResponse,
  OrderRegionDetailResponse,
} from '../../openapi/types';
import { cadastersList } from '../../openapi/operations/cadastersList';
import { obceList } from '../../openapi/operations/obceList';
import { orpList } from '../../openapi/operations/orpList';
import { krajeList } from '../../openapi/operations/krajeList';
import { momcList } from '../../openapi/operations/momcList';
import { useOrdersRetrieveResource } from '../../openapi/operations/ordersRetrieve';

import {
  actionSetIsPublic,
  actionSetStateDataValidityDate,
  actionSetChangeDataValidityDateFrom,
  actionSetDataValidity,
  actionSetSelectionType,
  actionSetPrimaryDB,
} from '../actions/newOrder';
import { OrderSelectionTypesEnum } from '../../openapi/types/OrderSelectionTypesEnum';
import type { OrderTypeEnum } from '../../openapi/types/OrderTypeEnum';

import { UNMANAGED_AREA_LAYERS } from '../../areas/constants';
import { actionSetPredefinedAreasFeatures } from '../../areas/actions';
import { ORDER_IS_PUBLIC } from '../../constants';

type MultiSelectEventData<T = string> = {
  values: T[];
  name?: string;
};

type OrderProps = {
  orderId?: string;
};

/**
 * transformace OrderRegionDetail objektu regions na polozky Multiselect form elementu
 * BE nemuze tuto transformaci delat z duvodu navaznosti
 * @param regions OrderRegionDetailResponse[]
 * @returns T[] (kraj, obec, orp, momc, cadaster)
 */
function getTransformedRegions<T>(regions: T[]): T[] {
  return regions.map((region) => {
    const { code, region_url, name } = region as OrderRegionDetailResponse;
    return {
      ...region,
      url: region_url,
    };
  });
}

export const NewOrder: React.FC<OrderProps> = (props) => {
  const orderId = props.orderId && parseInt(props.orderId, 10);
  const resource = useOrdersRetrieveResource(orderId ? { id: orderId } : null);
  const data = useMemo(() => {
    return (resource.data ? { ...resource.data } : null) as OrderRetrieveResponse;
  }, [resource.data]);

  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const [step, setStep] = useState(NEW_ORDER_STEPS.PUBLISH_SETS);
  const { isDirty } = useSelector((state) => state.newOrder);

  useBlocker(() => {
    if (isDirty) {
      const confirmLeave = window.confirm(formatMessage(newOrderMessages.submitConfirmMessage));
      if (confirmLeave) {
        return false;
      }
      return true;
    }
    return false;
  });

  const config = useSelector((state) => state.app.config.vydej) ?? {};
  const customerType = useSelector((state) => state.app.config.customerType);

  // INITIALIZE EDIT FORM
  // nastaveni vychozich hodnot do formulare
  useEffect(() => {
    if (!data) return;

    batch('NewOrder/Edit - initial form settings', () => {
      // ORDER ID - ID vydeje
      data.id && dispatch(actionSetId(`${data.id}`));

      // ORDER TYPE - Platnost
      data.order_type && dispatch(actionSetDataValidity(data.order_type as OrderTypeEnum));

      // FROM PRIMARY DB - Vydej z primarni DB
      data.from_primary_db && dispatch(actionSetPrimaryDB(data.from_primary_db));

      // IS PUBLIC - Dostupnost
      dispatch(
        actionSetIsPublic(data.is_public === true ? ORDER_IS_PUBLIC.TRUE : ORDER_IS_PUBLIC.FALSE),
      );

      // SELECTION TYPE - Typ vydeje
      dispatch(actionSetSelectionType(data.selection_type as OrderSelectionTypesEnum));
      data.order_data_changes_from &&
        dispatch(
          actionSetChangeDataValidityDateFrom(new Date(data.order_data_changes_from), null, null),
        );
      data.order_data_state_valid_at &&
        dispatch(
          actionSetStateDataValidityDate(new Date(data.order_data_state_valid_at), null, null),
        );

      //REASON
      data.ordering_reason && dispatch(actionSetReason(data.ordering_reason));

      dispatch(actionSetHandlingFee(!!data.handling_fee));
      dispatch(actionSetNote(data.note ?? ''));
      dispatch(actionSetOrigin(data.origin || null));

      // ATTACHMENTS
      data.attachments &&
        data.attachments.length &&
        dispatch(actionInitAttachments(data.attachments as AttachmentProps[]));

      // PUBLISHSETS
      data.publishsets.forEach((item) => {
        const publishsetUrl = item.publishset;
        item.required_formats.forEach((requiredFormat) => {
          dispatch(
            actionPublishsetAddFormat({
              publishsetUrl,
              formatUrl: requiredFormat.url,
            }),
          );
        });
      });

      // REGIONS
      const areaType = data.regions[0].type;
      const features = data.regions.map((region) => region.feature) as GeoJSONFeature[];

      // doplneni geometrie do pomocne mapove vrstvy
      if (areaType === ORDER_REGION_TYPES.CUSTOM) {
        // fix pro mapCore editace
        if (features.length) {
          features.forEach((feature) => {
            if (feature.properties && !feature.properties.id) feature.properties.id = feature.id;
          });
          dispatch(actionSetLayerFeatures(UNMANAGED_AREA_LAYERS.CUSTOM_AREAS, features));
        }
      } else {
        features.length && dispatch(actionSetPredefinedAreasFeatures(features));
      }
      // nastaveni aktivniho vyberoveho formulare podle prvniho zaznamu
      areaType && dispatch(actionSetAreaType(areaType));
    });
  }, [data]);

  // INITIALIZE CITY STATE
  const initCityItems = useMemo(() => {
    if (!data?.regions) return [];
    return data.regions.filter(
      (region) => region.type === ORDER_REGION_TYPES.OBEC,
    ) as unknown as ObecListResponse[];
  }, [data?.regions]);
  useEffect(() => {
    initCityItems.length && dispatch(actionSetCities(getTransformedRegions(initCityItems)));
  }, [initCityItems]);

  // INITIALIZE CADASTER STATE
  const initCadasterItems = useMemo(() => {
    if (!data?.regions) return [];
    return data.regions.filter(
      (region) => region.type === ORDER_REGION_TYPES.CADASTER,
    ) as unknown as CadasterListResponse[];
  }, [data?.regions]);
  useEffect(() => {
    initCadasterItems.length &&
      dispatch(actionSetCadasters(getTransformedRegions(initCadasterItems)));
  }, [initCadasterItems]);

  // INITIALIZE ORP STATE
  const initOrpItems = useMemo(() => {
    if (!data?.regions) return [];
    return data.regions.filter(
      (region) => region.type === ORDER_REGION_TYPES.ORP,
    ) as unknown as ORPListResponse[];
  }, [data?.regions]);
  useEffect(() => {
    initOrpItems.length && dispatch(actionSetORPs(getTransformedRegions(initOrpItems)));
  }, [initOrpItems]);

  // INITIALIZE KRAJ STATE
  const initRegionItems = useMemo(() => {
    if (!data?.regions) return [];
    return data.regions.filter(
      (region) => region.type === ORDER_REGION_TYPES.KRAJ,
    ) as unknown as KrajListResponse[];
  }, [data?.regions]);
  useEffect(() => {
    initRegionItems.length && dispatch(actionSetRegions(getTransformedRegions(initRegionItems)));
  }, [initRegionItems]);

  // INITIALIZE MOMC STATE
  const initMomcItems = useMemo(() => {
    if (!data?.regions) return [];
    return data.regions.filter(
      (region) => region.type === ORDER_REGION_TYPES.MOMC,
    ) as unknown as MomcListResponse[];
  }, [data?.regions]);
  useEffect(() => {
    initMomcItems.length && dispatch(actionSetMomcs(getTransformedRegions(initMomcItems)));
  }, [initMomcItems]);

  useEffect(
    () => () => {
      dispatch(newOrderClear());
    },
    [dispatch],
  );

  const handleNextStepClick = () => {
    let newStep = step + 1;
    // kontrola kroku REASON a pripadne preskoceni o krok vpred
    if (config.hideReason && newStep === NEW_ORDER_STEPS.REASON) {
      newStep++;
    }
    setStep(newStep);
  };

  const handlePrevStepClick = () => {
    let newStep = step - 1;
    // kontrola kroku REASON a pripadne preskoceni o krok zpet
    if (config.hideReason && newStep === NEW_ORDER_STEPS.REASON) {
      newStep--;
    }
    setStep(newStep);
  };

  const handleStepChange = (step: NEW_ORDER_STEPS) => {
    setStep(step);
  };

  const [hasMoreCadasterItems, setHasMoreCadasterItems] = useState(false);
  const prepareCadasterItems = async (name__icontains: string) => {
    const response = await cadastersList({ query: { name__icontains, page_size: 50 } });
    if (response.ok && response.data) {
      setHasMoreCadasterItems(!!(response.data.next || response.data.previous));
      return response.data.results;
    }
    return null;
  };
  const cadaster = useMultiSelect<
    OmittedFormItemProps | MultiSelectProps<CadasterListResponse>,
    CadasterListResponse
  >({
    name: 'cadaster',
    label: formatMessage(orderMessages.cadaster),
    initialValue: initCadasterItems,
    moreItemsMessage: hasMoreCadasterItems ? formatMessage(newOrderMessages.moreItems) : undefined,
    noItemsMessage: formatMessage(newOrderMessages.noItems),
    prepareItems: prepareCadasterItems,
    renderValue: (item) => item.name,
    renderItem: (item) => item.name,
    onChange: (e: any, eventData: MultiSelectEventData<CadasterListResponse>) =>
      dispatch(actionSetCadasters(eventData.values)),
  });

  const [hasMoreCityItems, setHasMoreCityItems] = useState(false);
  const prepareCityItems = async (name__icontains: string) => {
    const response = await obceList({ query: { name__icontains } });
    if (response.ok && response.data) {
      setHasMoreCityItems(!!(response.data.next || response.data.previous));
      return response.data.results;
    }
    return null;
  };

  const city = useMultiSelect<
    OmittedFormItemProps | MultiSelectProps<ObecListResponse>,
    ObecListResponse
  >({
    name: 'city',
    label: formatMessage(orderMessages.city),
    initialValue: initCityItems,
    moreItemsMessage: hasMoreCityItems ? formatMessage(newOrderMessages.moreItems) : undefined,
    noItemsMessage: formatMessage(newOrderMessages.noItems),
    prepareItems: prepareCityItems,
    renderValue: (item) => item.name,
    renderItem: (item) => item.name,
    onChange: (e: any, eventData: MultiSelectEventData<ObecListResponse>) =>
      dispatch(actionSetCities(eventData.values)),
  });

  const [hasMoreORPItems, setHasMoreORPItems] = useState(false);
  const prepareORPItems = async (name__icontains: string) => {
    const response = await orpList({ query: { name__icontains } });
    if (response.ok && response.data) {
      setHasMoreORPItems(!!(response.data.next || response.data.previous));
      return response.data.results;
    }
    return null;
  };
  const orp = useMultiSelect<
    OmittedFormItemProps | MultiSelectProps<ORPListResponse>,
    ORPListResponse
  >({
    name: 'orp',
    label: formatMessage(orderMessages.orp),
    initialValue: initOrpItems,
    moreItemsMessage: hasMoreORPItems ? formatMessage(newOrderMessages.moreItems) : undefined,
    noItemsMessage: formatMessage(newOrderMessages.noItems),
    prepareItems: prepareORPItems,
    renderValue: (item) => item.name,
    renderItem: (item) => item.name,
    onChange: (e: any, eventData: MultiSelectEventData<ORPListResponse>) =>
      dispatch(actionSetORPs(eventData.values)),
  });

  const prepareRegionItems = async (name__icontains: string) => {
    const response = await krajeList({ query: { name__icontains } });
    if (response.ok && response.data) {
      return response.data.results;
    }
    return null;
  };
  const region = useMultiSelect<
    OmittedFormItemProps | MultiSelectProps<KrajListResponse>,
    KrajListResponse
  >({
    name: 'region',
    label: formatMessage(orderMessages.region),
    initialValue: initRegionItems,
    prepareItems: prepareRegionItems,
    renderValue: (item) => item.name,
    renderItem: (item) => item.name,
    onChange: (e: any, eventData: MultiSelectEventData<KrajListResponse>) =>
      dispatch(actionSetRegions(eventData.values)),
  });

  const [hasMoreMOMCItems, setHasMoreMOMCItems] = useState(false);
  const prepareMomcItems = async (name__icontains: string) => {
    const response = await momcList({ query: { name__icontains } });

    if (response.ok && response.data) {
      setHasMoreMOMCItems(!!(response.data.next || response.data.previous));
      return response.data.results;
    }
    return null;
  };
  const momc = useMultiSelect<
    OmittedFormItemProps | MultiSelectProps<MomcListResponse>,
    MomcListResponse
  >({
    name: 'momc',
    label: formatMessage(orderMessages.momc),
    initialValue: initMomcItems,
    moreItemsMessage: hasMoreMOMCItems ? formatMessage(newOrderMessages.moreItems) : undefined,
    noItemsMessage: formatMessage(newOrderMessages.noItems),
    prepareItems: prepareMomcItems,
    renderValue: (item) => item.name,
    renderItem: (item) => item.name,
    onChange: (e: any, eventData: MultiSelectEventData<MomcListResponse>) =>
      dispatch(actionSetMomcs(eventData.values)),
  });

  const handleChange = useCallback(
    (clearAreaType: boolean) => {
      if (clearAreaType) {
        dispatch(actionSetAreaType(''));
      }
      dispatch(actionClearAreas());
      [cadaster, city, orp, region, momc].forEach((formItem) => formItem.clear());
    },
    [cadaster, city, dispatch, orp, region, momc],
  );

  return (
    <div className='dmx-newOrder'>
      <div className='dmx-newOrder--inner'>
        <div className='dmx-newOrder--header'>
          <PageTitle>
            <FormattedMessage {...newOrderMessages.pageTitle} />
          </PageTitle>
        </div>
        <ol className='dmx-newOrder--nav'>
          <StepNavItem
            isActive={step === NEW_ORDER_STEPS.PUBLISH_SETS}
            title={formatMessage(newOrderMessages.publishSetsStepTitle)}
            onClick={() => handleStepChange(NEW_ORDER_STEPS.PUBLISH_SETS)}
          />
          <StepNavItem
            isActive={step === NEW_ORDER_STEPS.REGIONS}
            title={formatMessage(newOrderMessages.selectRegionStepTitle)}
            onClick={() => handleStepChange(NEW_ORDER_STEPS.REGIONS)}
          />
          {!config.hideReason && (
            <StepNavItem
              isActive={step === NEW_ORDER_STEPS.REASON}
              title={formatMessage(
                customerType === 'ipr'
                  ? newOrderMessages.detailsStepTitle
                  : newOrderMessages.setReasonStepTitle,
              )}
              onClick={() => handleStepChange(NEW_ORDER_STEPS.REASON)}
            />
          )}
          <StepNavItem
            isActive={step === NEW_ORDER_STEPS.SUMMARY}
            title={formatMessage(newOrderMessages.summaryTitle)}
            onClick={() => handleStepChange(NEW_ORDER_STEPS.SUMMARY)}
          />
        </ol>
        <ul className='sg-a-p-r'>
          <Step isActive={step === NEW_ORDER_STEPS.PUBLISH_SETS}>
            <OrderPublishsetList onNextStepClick={handleNextStepClick} onChange={handleChange} />
          </Step>
          <Step isActive={step === NEW_ORDER_STEPS.REGIONS}>
            <Areas
              cadaster={cadaster}
              city={city}
              orp={orp}
              region={region}
              momc={momc}
              onPrevStepClick={handlePrevStepClick}
              onNextStepClick={handleNextStepClick}
              onChange={handleChange}
            />
          </Step>
          {!config.hideReason && (
            <Step isActive={step === NEW_ORDER_STEPS.REASON}>
              <Reason onPrevStepClick={handlePrevStepClick} onNextStepClick={handleNextStepClick} />
            </Step>
          )}
          <Step isActive={step === NEW_ORDER_STEPS.SUMMARY}>
            <Summary onStepChange={handleStepChange} onPrevStepClick={handlePrevStepClick} />
          </Step>
        </ul>
      </div>
    </div>
  );
};
